import React, { useState, useEffect, useRef } from "react";
import "./versionhistory.css";
import { baseRequest } from "../../../axiosconfig";
import Loading from "../../loading";
import { useReactToPrint } from "react-to-print";
import { PRINT_PAGE_STYLE } from "../../../utils/printPageStyle";
import { formatResponsibilityRow } from "../../../utils/responsibility";

// Osoba koja je napravila izmenu: ime i prezime zaposlenog, inače imejl
// (isto pravilo kao Evidencija aktivnosti); za deaktiviran nalog originalni imejl
const userDisplayName = (user) => {
  const emp = user?.employee;
  const name = `${emp?.first_name || ""} ${emp?.last_name || ""}`.trim();
  return name || user?.original_email || user?.email || "—";
};

const ENTITY_LABELS = {
  process_map: "MAPA PROCESA",
  procedure: "PROCEDURA",
};

// Srpske labele za polja snapshota (procedura + mapa procesa)
const FIELD_LABELS = {
  code: "Šifra",
  name: "Naziv",
  version: "Verzija",
  company_name: "Naziv preduzeća",
  organization_unit_code: "Šifra organizacione jedinice",
  process_goal: "Cilj procesa",
  potential_risks: "Potencijalni rizici",
  description_input: "Ulaz",
  description_action: "Aktivnosti",
  description_output: "Izlaz",
  correlation_to_other_processes: "Veza sa drugim procesima",
  resources: "Resursi",
  procedure_area: "Oblast",
  laws_and_regulations: "Zakonska i druga regulativa",
  terms_and_abbreviations: "Termini i skraćenice",
  other_documentation: "Ostala dokumentacija",
  responsibility_authorization: "Odgovornosti i ovlašćenja",
};

function formatValue(key, value) {
  if (value === null || value === undefined || value === "") return "—";
  if (key === "responsibility_authorization") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed
          .map(formatResponsibilityRow)
          .filter(Boolean)
          .join("; ");
      }
    } catch (e) {
      // prikaži sirovu vrednost
    }
  }
  if (key === "correlation_to_other_processes") {
    // JSON niz naziva (isto kao štampa mape); legacy običan tekst ili
    // neispravan JSON ostaju doslovno, prazan niz se prikazuje kao „—"
    const trimmed = String(value).trim();
    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          const names = parsed.filter(Boolean).join(", ");
          return names || "—";
        }
      } catch (e) {
        // prikaži sirovu vrednost
      }
    }
    return trimmed || "—";
  }
  return String(value);
}

function VersionHistory({ entityType, entityId, onClose }) {
  const [loading, setLoading] = useState(false);
  const [versions, setVersions] = useState([]);
  const [openVersion, setOpenVersion] = useState(null);
  const printRef = useRef();

  const handlePrintVersion = useReactToPrint({
    content: () => printRef.current,
    pageStyle: PRINT_PAGE_STYLE,
  });

  useEffect(() => {
    setLoading(true);
    baseRequest
      .get(`/document_versions/${entityType}/${entityId}`)
      .then((res) => {
        setVersions(res.data.document_versions || []);
        setLoading(false);
      })
      .catch(() => {
        setVersions([]);
        setLoading(false);
      });
  }, [entityType, entityId]);

  const handleOpenVersion = (version) => {
    if (openVersion?.id === version.id) {
      setOpenVersion(null);
      return;
    }
    setLoading(true);
    baseRequest
      .get(`/document_version/${version.id}`)
      .then((res) => {
        setOpenVersion(res.data.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const formatDateTime = (d) =>
    `${new Date(d).toLocaleDateString("sr-RS")} ${new Date(d).toLocaleTimeString("sr-RS", { hour: "2-digit", minute: "2-digit" })}`;

  return (
    <div className="version-history-overlay" onClick={onClose}>
      <div className="version-history" onClick={(e) => e.stopPropagation()}>
        <div className="version-history-header">
          <h3>ISTORIJA VERZIJA</h3>
          <button className="version-history-close" onClick={onClose}>
            ✕
          </button>
        </div>
        <p className="version-history-note">
          Svako snimanje automatski čuva prethodno stanje dokumenta kao
          arhiviranu verziju. Kliknite na red da vidite sadržaj te verzije ili
          da je odštampate.
        </p>

        {versions.length === 0 && !loading && (
          <p className="version-history-empty">
            Nema sačuvanih verzija. Verzija se beleži pri svakoj izmeni
            dokumenta.
          </p>
        )}

        {versions.length > 0 && (
          <table className="version-history-table">
            <thead>
              <tr>
                <th>VERZIJA</th>
                <th>IZMENIO</th>
                <th>VAŽILA DO</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <React.Fragment key={v.id}>
                  <tr
                    className={
                      openVersion?.id === v.id
                        ? "version-history-row version-history-row-active"
                        : "version-history-row"
                    }
                    onClick={() => handleOpenVersion(v)}
                  >
                    <td>{v.version_label || "bez oznake"}</td>
                    <td>{userDisplayName(v.user)}</td>
                    <td>{formatDateTime(v.created_at)}</td>
                  </tr>
                  {openVersion?.id === v.id && openVersion.snapshot && (
                    <tr>
                      <td colSpan={3}>
                        <div className="version-history-snapshot">
                          <div className="version-history-snapshot-actions">
                            <button
                              className="add-poslovni-proces-btn"
                              onClick={handlePrintVersion}
                            >
                              <img alt="" src="/images/printer.png" />
                              <span>Odštampaj ovu verziju</span>
                            </button>
                          </div>
                          {Object.entries(FIELD_LABELS).map(([key, label]) => {
                            if (!(key in openVersion.snapshot)) return null;
                            return (
                              <div key={key} className="version-history-snapshot-row">
                                <span className="version-history-snapshot-label">
                                  {label}:
                                </span>
                                <span>{formatValue(key, openVersion.snapshot[key])}</span>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}

        <Loading isLoading={loading} />

        {/* Štampana verzija otvorenog snimka */}
        <div style={{ display: "none" }}>
          <div ref={printRef} className="version-history-print">
            {openVersion && openVersion.snapshot && (
              <>
                <h2>
                  {ENTITY_LABELS[entityType] || "DOKUMENT"}: ARHIVIRANA VERZIJA
                </h2>
                <p>
                  Verzija: {openVersion.version_label || "bez oznake"}
                  <br />
                  Važila do: {formatDateTime(openVersion.created_at)}
                  <br />
                  Izmenio: {userDisplayName(openVersion.user)}
                </p>
                {Object.entries(FIELD_LABELS).map(([key, label]) => {
                  if (!(key in openVersion.snapshot)) return null;
                  return (
                    <div key={key} className="version-history-print-row">
                      <strong>{label}:</strong>{" "}
                      {formatValue(key, openVersion.snapshot[key])}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default VersionHistory;
